/**
 * WhatsApp Sales Intelligence — Phases 1-11 Management Console (iter272).
 *
 * DEMO / PRE-PILOT.  Meta Cloud API is NOT connected.
 *
 * Tabs:
 *   • Summary       — Yesterday tile + Handling Score + Diagnosis
 *   • Funnel        — canonical funnel + category breakdown
 *   • Centres       — centre comparison (no ranking; RAG only)
 *   • Inbox         — 3-panel Central Inbox with rich filters + search
 *   • Alerts        — Sales-leakage alerts (acknowledge / resolve / dismiss)
 *   • Matches       — Booking match panel (Table / Catering / Event)
 *   • Reports       — PDF + Excel download
 *   • Integration   — Settings scaffold (no secrets returned)
 *   • Pilot         — Readiness checklist + Management approval
 *
 * Every tab reads from the SAME backend canonical calc engine.
 * The frontend NEVER re-computes tile / funnel / handling-score values.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { MetaDiagnosticsPanel } from "@/components/whatsapp/MetaDiagnosticsPanel";
import GuestContactsTab from "@/components/whatsapp/guests/GuestContactsTab";
import AiReceptionistTab from "@/components/whatsapp/receptionist/AiReceptionistTab";
import {
  MessageSquare, RefreshCw, AlertTriangle, CheckCircle2, Trash2,
  Send, Info, Download, ShieldAlert, ShieldCheck, Filter,
  ArrowDownWideNarrow, Clock, Settings2, Activity, Loader2,
} from "lucide-react";

const _FMT = (val, sym = "₹") =>
  `${sym}${Number(val || 0).toLocaleString(sym === "₹" ? "en-IN" : "en-AU",
    { maximumFractionDigits: 2 })}`;

const _STATUS_BADGE = {
  NEW: "bg-blue-100 text-blue-800 border-blue-300",
  REPLIED: "bg-cyan-100 text-cyan-800 border-cyan-300",
  INTERESTED: "bg-indigo-100 text-indigo-800 border-indigo-300",
  LINK_SENT: "bg-purple-100 text-purple-800 border-purple-300",
  FOLLOW_UP: "bg-amber-100 text-amber-800 border-amber-300",
  BOOKED: "bg-emerald-100 text-emerald-800 border-emerald-300",
  ORDERED: "bg-emerald-100 text-emerald-800 border-emerald-300",
  CONVERTED: "bg-green-200 text-green-900 border-green-400",
  LOST: "bg-red-100 text-red-800 border-red-300",
  UNRESOLVED: "bg-slate-200 text-slate-800 border-slate-300",
  NOT_A_SALES_LEAD: "bg-neutral-100 text-neutral-700 border-neutral-300",
};

const _SEV_BADGE = {
  RED: "bg-red-100 text-red-800 border-red-300",
  AMBER: "bg-amber-100 text-amber-800 border-amber-300",
  INFO: "bg-sky-100 text-sky-800 border-sky-300",
};

const CATEGORIES = ["TABLE","CATERING","MODAK","PICKUP","TIFFIN","WEDDING",
  "KELVAN","MANGALAGAUR","CELEBRATION","CORPORATE","GROUP","MENU_PRICE",
  "LOCATION","GENERAL","OTHER","COMPLAINT","FRANCHISE"];

const STATUSES = ["NEW","REPLIED","INTERESTED","LINK_SENT","FOLLOW_UP",
  "BOOKED","ORDERED","CONVERTED","LOST","UNRESOLVED","NOT_A_SALES_LEAD"];

const LOST_REASONS = ["PRICE","NO_TABLE_AVAILABLE","ITEM_NOT_AVAILABLE",
  "SOLD_OUT","DATE_NOT_AVAILABLE","DELIVERY_ISSUE","LOCATION_TOO_FAR",
  "RESPONSE_TOO_LATE","NO_RESPONSE_FROM_CENTRE","CUSTOMER_CHANGED_MIND",
  "CATERING_CAPACITY","COMPETITOR","CUSTOMER_NOT_RESPONDING","OTHER"];

function Tile({ label, value, sub, tone = "" }) {
  return (
    <div className={`rounded-lg border p-3 bg-white ${tone}`}
      data-testid={`wi-tile-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="text-xs text-slate-500 uppercase tracking-wide">{label}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Root page
// ─────────────────────────────────────────────────────────────
export default function WhatsAppSalesIntelligence({ session }) {
  const search = new URLSearchParams(window.location.search);
  const isAdmin = !!(session?.is_super_admin || session?.is_admin);
  const [tab, setTab] = useState(search.get("tab") || "summary");
  const [center, setCenter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [includeDemo, setIncludeDemo] = useState(true);

  const [tiles, setTiles] = useState(null);
  const [yesterday, setYesterday] = useState(null);
  const [centres, setCentres] = useState([]);        // canonical operational-centre master
  const [centreMetrics, setCentreMetrics] = useState([]);  // enriched metrics per centre
  const [diag, setDiag] = useState(null);
  const [score, setScore] = useState(null);
  const [loading, setLoading] = useState(false);

  // Canonical dropdown source — the same operational centre master used
  // across IntraPB, intersected with the user's authorised centres.  Reads
  // `db.centers` (NOT `whatsapp_leads` / `whatsapp_messages`) so a
  // configured centre appears even with zero WhatsApp activity.
  useEffect(() => {
    if (!session?.token) return;
    (async () => {
      try {
        const r = await api.post("/whatsapp-intel/centres/available",
          { token: session.token });
        setCentres(r.data.rows || []);
      } catch (e) { /* silent — header refresh happens next */ }
    })();
  }, [session?.token]);

  const loadHeader = async () => {
    if (!session?.token) return;
    setLoading(true);
    try {
      const filters = { token: session.token, center: center || null,
        date_from: dateFrom || null, date_to: dateTo || null,
        include_demo: includeDemo };
      const [t, cc, d, s, y] = await Promise.all([
        api.post("/whatsapp-intel/dashboard/tiles", filters),
        api.post("/whatsapp-intel/reports/centre-comparison", filters),
        api.post("/whatsapp-intel/dashboard/diagnosis", filters),
        api.post("/whatsapp-intel/dashboard/handling-score", filters),
        api.post("/whatsapp-intel/dashboard/yesterday",
          { token: session.token, center: center || null,
            include_demo: includeDemo }),
      ]);
      setTiles(t.data.metrics);
      setCentreMetrics(cc.data.rows || []);
      setDiag(d.data.diagnosis);
      setScore(s.data.score);
      setYesterday(y.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Load failed");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadHeader(); /* eslint-disable-line */ },
    [center, dateFrom, dateTo, includeDemo]);

  const currentSymbol = useMemo(() => {
    const c = centres.find((r) => r.center === center);
    if (c?.currency === "AUD") return "A$";
    return "₹";
  }, [centres, center]);

  const seedDemo = async () => {
    if (!window.confirm("Seed demo WhatsApp Intelligence data?")) return;
    try {
      const r = await api.post("/whatsapp-intel/demo/seed",
        { token: session.token });
      toast.success(`Seeded ${r.data.conversations} conversations`);
      loadHeader();
    } catch (e) { toast.error(e.response?.data?.detail || "Seed failed"); }
  };
  const resetDemo = async () => {
    if (!window.confirm("Delete ALL demo data? Real data is untouched.")) return;
    try {
      await api.post("/whatsapp-intel/demo/reset", { token: session.token });
      toast.success("Demo data cleared");
      loadHeader();
    } catch (e) { toast.error(e.response?.data?.detail || "Reset failed"); }
  };

  const evalAlerts = async () => {
    try {
      const r = await api.post("/whatsapp-intel/alerts/run",
        { token: session.token, include_demo: includeDemo });
      toast.success(`Alerts refreshed — production ${r.data.production.created}, demo ${r.data.demo.created}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-4 space-y-4" data-testid="whatsapp-intel-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-6 h-6 text-emerald-700" />
          <h1 className="text-xl font-semibold">WhatsApp Sales Intelligence</h1>
          <Badge className="bg-amber-100 text-amber-900 border-amber-300 ml-2">
            LIVE ARCHITECTURE READY · META CONNECTION: NOT ACTIVE
          </Badge>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={center || "ALL"} onValueChange={(v) => setCenter(v === "ALL" ? "" : v)}>
            <SelectTrigger className="w-56" data-testid="wi-center-select">
              <SelectValue placeholder="All centres" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All centres</SelectItem>
              {centres.map((r) => (
                <SelectItem key={r.center} value={r.center}>
                  {r.center} — {r.integration_status_label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="w-36" data-testid="wi-date-from" />
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="w-36" data-testid="wi-date-to" />
          <div className="flex gap-1" data-testid="wi-date-quick">
            <Button size="sm" variant="outline" data-testid="wi-date-today"
              onClick={() => { const t = new Date().toISOString().slice(0,10); setDateFrom(t); setDateTo(t); }}>
              Today
            </Button>
            <Button size="sm" variant="outline" data-testid="wi-date-yesterday"
              onClick={() => { const y = new Date(Date.now()-864e5).toISOString().slice(0,10); setDateFrom(y); setDateTo(y); }}>
              Yesterday
            </Button>
            <Button size="sm" variant="outline" data-testid="wi-date-week"
              onClick={() => {
                const now = new Date(); const day = now.getDay() || 7;
                const monday = new Date(now); monday.setDate(now.getDate() - (day - 1));
                setDateFrom(monday.toISOString().slice(0,10));
                setDateTo(new Date().toISOString().slice(0,10));
              }}>This Week</Button>
            <Button size="sm" variant="outline" data-testid="wi-date-month"
              onClick={() => {
                const d = new Date();
                setDateFrom(new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0,10));
                setDateTo(new Date().toISOString().slice(0,10));
              }}>This Month</Button>
            <Button size="sm" variant="ghost" data-testid="wi-date-clear"
              onClick={() => { setDateFrom(""); setDateTo(""); }}>Clear</Button>
          </div>
          <label className="text-xs flex items-center gap-1 border rounded px-2 py-1">
            <input type="checkbox" checked={includeDemo}
              onChange={(e) => setIncludeDemo(e.target.checked)}
              data-testid="wi-include-demo" />
            Include demo
          </label>
          <Button size="sm" variant="outline" onClick={loadHeader} disabled={loading}
            data-testid="wi-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          {isAdmin && (
            <>
              <Button size="sm" onClick={seedDemo} data-testid="wi-seed-btn">
                Seed Demo
              </Button>
              <Button size="sm" variant="outline" onClick={resetDemo}
                data-testid="wi-reset-btn">
                <Trash2 className="w-4 h-4 mr-1" />Reset Demo
              </Button>
              <Button size="sm" variant="outline" onClick={evalAlerts}
                data-testid="wi-run-alerts-btn">
                <ShieldAlert className="w-4 h-4 mr-1" />Run Alerts
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Demo banner */}
      {includeDemo && (
        <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-center gap-2"
          data-testid="wi-demo-banner">
          <Info className="w-4 h-4" />
          <strong>DEMO DATA visible.</strong>
          Production dashboards exclude demo rows by default — un-check <em>Include demo</em>
          to view only real WhatsApp Intelligence data.
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap gap-1 w-fit">
          <TabsTrigger value="summary" data-testid="wi-tab-summary">Summary</TabsTrigger>
          <TabsTrigger value="funnel" data-testid="wi-tab-funnel">Funnel</TabsTrigger>
          <TabsTrigger value="centres" data-testid="wi-tab-centres">Centres</TabsTrigger>
          <TabsTrigger value="inbox" data-testid="wi-tab-inbox">Inbox</TabsTrigger>
          <TabsTrigger value="alerts" data-testid="wi-tab-alerts">Alerts</TabsTrigger>
          <TabsTrigger value="reports" data-testid="wi-tab-reports">Reports</TabsTrigger>
          <TabsTrigger value="guests" data-testid="wi-tab-guests">Guest Contacts</TabsTrigger>
          <TabsTrigger value="receptionist" data-testid="wi-tab-receptionist">AI Receptionist</TabsTrigger>
          <TabsTrigger value="integration" data-testid="wi-tab-integration">Integration</TabsTrigger>
          <TabsTrigger value="pilot" data-testid="wi-tab-pilot">Pilot Readiness</TabsTrigger>
        </TabsList>

        <TabsContent value="summary">
          {center && tiles && tiles.total_enquiries === 0 && (
            <div className="mb-3 rounded border border-slate-300 bg-slate-50 p-3 text-sm"
                  data-testid="wi-summary-no-activity">
              <strong>{center}</strong> — No WhatsApp activity for the selected
              period.  The centre is available for filtering; data will appear
              here once messages start arriving.
            </div>
          )}
          <SummaryTab tiles={tiles} yesterday={yesterday} diag={diag}
            score={score} currencySymbol={currentSymbol} setTab={setTab}
            dateFrom={dateFrom} dateTo={dateTo} />
        </TabsContent>

        <TabsContent value="funnel">
          <FunnelTab session={session} filters={{ center, dateFrom, dateTo, includeDemo }} />
        </TabsContent>

        <TabsContent value="centres">
          <CentresTab rows={centreMetrics} onPickCenter={(c) => { setCenter(c); setTab("summary"); }} />
        </TabsContent>

        <TabsContent value="inbox">
          <InboxTab session={session} filters={{ center, dateFrom, dateTo, includeDemo }}
            currencySymbol={currentSymbol} onDashboardRefresh={loadHeader} />
        </TabsContent>

        <TabsContent value="alerts">
          <AlertsTab session={session} filters={{ center, dateFrom, dateTo, includeDemo }} />
        </TabsContent>

        <TabsContent value="reports">
          <ReportsTab session={session} filters={{ center, dateFrom, dateTo, includeDemo }} />
        </TabsContent>

        <TabsContent value="guests">
          <GuestContactsTab session={session} />
        </TabsContent>

        <TabsContent value="receptionist">
          <AiReceptionistTab session={session} />
        </TabsContent>

        <TabsContent value="integration">
          <IntegrationTab session={session} isAdmin={isAdmin} />
        </TabsContent>

        <TabsContent value="pilot">
          <PilotTab session={session} isAdmin={isAdmin} />
        </TabsContent>
      </Tabs>

      <div className="text-[10px] text-slate-500 italic text-center pt-2 border-t">
        Potential Revenue Lost is an opportunity estimate and is not an accounting loss.
        WhatsApp Intelligence does not modify Sales, Expenses, Franchise Settlement or GST records.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Summary Tab — dynamic heading following selected From/To
// ─────────────────────────────────────────────────────────────
function _summaryHeading(dateFrom, dateTo) {
  const today = new Date().toISOString().slice(0, 10);
  const yesterday = new Date(Date.now() - 864e5).toISOString().slice(0, 10);
  if (!dateFrom && !dateTo) return "WhatsApp Summary — All time";
  if (dateFrom && dateTo && dateFrom === dateTo) {
    if (dateFrom === today) return `Today's WhatsApp Summary — ${dateFrom}`;
    if (dateFrom === yesterday) return `Yesterday's WhatsApp Summary — ${dateFrom}`;
    return `WhatsApp Summary — ${dateFrom}`;
  }
  return `WhatsApp Summary — ${dateFrom || "…"} to ${dateTo || "…"}`;
}

function SummaryTab({ tiles, yesterday, diag, score, currencySymbol, setTab,
                        dateFrom, dateTo }) {
  const heading = _summaryHeading(dateFrom, dateTo);
  return (
    <div className="space-y-3">
      {/* PRIMARY: selected-period Summary — always follows From/To */}
      <Card data-testid="wi-primary-summary" className="border-emerald-300">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="w-5 h-5 text-emerald-700" />
            {heading}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {tiles ? (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
              <Tile label="Total Enquiries" value={tiles.total_enquiries} />
              <Tile label="Valid Leads" value={tiles.valid_sales_leads} />
              <Tile label="Answered" value={tiles.answered} />
              <Tile label="Unanswered" value={tiles.unanswered}
                    tone={tiles.unanswered ? "border-red-300" : ""} />
              <Tile label="Converted" value={tiles.converted} />
              <Tile label="Lost" value={tiles.lost} />
              <Tile label="Unresolved" value={tiles.unresolved} />
              <Tile label="Conversion %" value={`${tiles.conversion_pct}%`} />
              <Tile label="Avg First Response"
                    value={tiles.avg_first_response_minutes != null
                            ? `${tiles.avg_first_response_minutes} m` : "—"} />
              <Tile label="Median First Response"
                    value={tiles.median_first_response_minutes != null
                            ? `${tiles.median_first_response_minutes} m` : "—"} />
              <Tile label="P90 Response"
                    value={tiles.p90_first_response_minutes != null
                            ? `${tiles.p90_first_response_minutes} m` : "—"} />
              <Tile label={`Waiting > ${tiles.response_threshold_minutes}m`}
                    value={tiles.waiting_over_threshold}
                    tone={tiles.waiting_over_threshold ? "border-red-300" : ""} />
              <Tile label="Follow-ups Due" value={tiles.follow_ups_due} />
              <Tile label="Potential Rev. Lost"
                    value={_FMT(tiles.potential_revenue_lost, currencySymbol)}
                    sub="Sales-intel only" />
              {score && score.band === "INSUFFICIENT_DATA" ? (
                <Tile label="Handling Score" value="INSUFFICIENT DATA"
                        sub={`sample ${score.sample_size}/${score.minimum_sample_size}`} />
              ) : score ? (
                <Tile label="Handling Score"
                        value={`${score.score}/100`} sub={score.band} />
              ) : null}
            </div>
          ) : (
            <div className="text-slate-500 text-sm">Loading…</div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {diag && (
          <Card data-testid="wi-diagnosis-card">
            <CardHeader>
              <CardTitle className="text-base">Why Are We (Not) Converting?</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="text-lg font-semibold">
                {diag.diagnosis === "INSUFFICIENT_DATA" ? (
                  <span className="text-slate-500 flex items-center gap-1">
                    <Info className="w-4 h-4" /> Insufficient data
                  </span>
                ) : diag.diagnosis}
              </div>
              <div className="text-xs text-slate-600">
                <div><strong>Metric:</strong> {diag.metric}</div>
                <div><strong>Threshold:</strong> {diag.threshold}</div>
                <div><strong>Actual:</strong> {diag.actual}</div>
                <div><strong>Period:</strong> {diag.period}</div>
              </div>
            </CardContent>
          </Card>
        )}
        {score && (
          <Card data-testid="wi-score-card">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Handling Score
                <Button variant="link" size="sm" className="ml-2 px-0"
                  onClick={() => window.alert(
                    "The Handling Score is a transparent 5-component index:\n" +
                    "• Response handling (30) — median FRT vs threshold\n" +
                    "• Unanswered control (20) — % answered\n" +
                    "• Follow-up discipline (20) — % follow-ups completed\n" +
                    "• Relevant action (15) — % of valid leads reaching LINK_SENT+\n" +
                    "• Outcome recording (15) — closed leads with proper reason/match\n\n" +
                    "Below the minimum sample size, the score is INSUFFICIENT DATA " +
                    "instead of a GREEN/AMBER/RED band.")}
                  data-testid="wi-score-help">How is this calculated?</Button>
              </CardTitle>
              <Badge className={
                score.band === "INSUFFICIENT_DATA"
                  ? "bg-slate-100 text-slate-700 border-slate-300" :
                score.band === "GREEN" ? "bg-emerald-100 text-emerald-800 border-emerald-300" :
                  score.band === "AMBER" ? "bg-amber-100 text-amber-800 border-amber-300" :
                    "bg-red-100 text-red-800 border-red-300"}>
                {score.band === "INSUFFICIENT_DATA"
                  ? `INSUFFICIENT DATA (${score.sample_size}/${score.minimum_sample_size})`
                  : `${score.score} / 100 · ${score.band}`}
              </Badge>
            </CardHeader>
            <CardContent>
              <table className="w-full text-xs">
                <tbody>
                  {score.breakdown.map((b) => (
                    <tr key={b.component} className="border-b">
                      <td className="py-1">{b.component.replace(/_/g, " ")}</td>
                      <td className="text-right"><strong>{b.earned}</strong> / {b.max}</td>
                      <td className="pl-2 text-slate-500">{b.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </div>

      {/* SECONDARY: Yesterday snapshot — additional insight only */}
      {yesterday && (
        <Card data-testid="wi-yesterday-tile"
              className="border-slate-200 bg-slate-50/60">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-slate-700">
              <Info className="w-4 h-4" />
              Additional insight — Yesterday's opportunities ({yesterday.date})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <Tile label="Enquiries" value={yesterday.metrics.total_enquiries} />
              <Tile label="Valid Leads" value={yesterday.metrics.valid_sales_leads} />
              <Tile label="Converted" value={yesterday.metrics.converted} />
              <Tile label="Unanswered" value={yesterday.metrics.unanswered} />
              <Tile label="Lost" value={yesterday.metrics.lost} />
            </div>
            <div className="text-[10px] text-slate-500 italic mt-2">{yesterday.note}</div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Funnel Tab
// ─────────────────────────────────────────────────────────────
function FunnelTab({ session, filters }) {
  const [funnel, setFunnel] = useState(null);
  const [category, setCategory] = useState("");
  const load = async () => {
    try {
      const r = await api.post("/whatsapp-intel/reports/funnel", {
        token: session.token, center: filters.center || null,
        date_from: filters.dateFrom || null, date_to: filters.dateTo || null,
        category: category || null, include_demo: filters.includeDemo,
      });
      setFunnel(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [
    filters.center, filters.dateFrom, filters.dateTo, filters.includeDemo, category]);

  if (!funnel) return <div className="text-sm text-slate-500 p-4">Loading…</div>;
  const maxCount = Math.max(1, ...funnel.stages.map((s) => s.count));
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Label className="text-xs">Category filter</Label>
        <Select value={category || "ALL"}
          onValueChange={(v) => setCategory(v === "ALL" ? "" : v)}>
          <SelectTrigger className="w-40" data-testid="wi-funnel-category">
            <SelectValue placeholder="All categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All categories</SelectItem>
            {CATEGORIES.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Card data-testid="wi-funnel-card">
        <CardHeader><CardTitle className="text-base">Sales Funnel</CardTitle></CardHeader>
        <CardContent className="space-y-1">
          {funnel.stages.map((s) => (
            <div key={s.stage} className="flex items-center gap-2 text-sm"
              data-testid={`wi-funnel-stage-${s.stage.replace(/\W+/g,"-").toLowerCase()}`}>
              <div className="w-40">{s.stage}</div>
              <div className="flex-1 bg-slate-100 rounded h-5 relative overflow-hidden">
                <div className="bg-emerald-500 h-5" style={{ width: `${s.count * 100 / maxCount}%` }} />
              </div>
              <div className="w-16 text-right"><strong>{s.count}</strong></div>
            </div>
          ))}
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-base">Lead Category Breakdown</CardTitle></CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead><tr className="bg-muted/50 border-b">
              <th className="text-left p-2">Category</th>
              <th className="text-right p-2">Enquiries</th>
              <th className="text-right p-2">Converted</th>
              <th className="text-right p-2">Lost</th>
            </tr></thead>
            <tbody>
              {funnel.by_category.map((c) => (
                <tr key={c.category} className="border-b">
                  <td className="p-2">{c.category}</td>
                  <td className="p-2 text-right">{c.enquiries}</td>
                  <td className="p-2 text-right">{c.converted}</td>
                  <td className="p-2 text-right">{c.lost}</td>
                </tr>
              ))}
              {funnel.by_category.length === 0 && (
                <tr><td colSpan={4} className="p-4 text-center text-slate-500">No data.</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Centres Tab
// ─────────────────────────────────────────────────────────────
function CentresTab({ rows, onPickCenter }) {
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">
        Centre Comparison — no ranking. Metrics only.</CardTitle></CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted/50 border-b">
              <th className="text-left p-2">Centre</th>
              <th className="text-right p-2">Curr.</th>
              <th className="text-right p-2">Enquiries</th>
              <th className="text-right p-2">Valid</th>
              <th className="text-right p-2">Answ.</th>
              <th className="text-right p-2">Unans.</th>
              <th className="text-right p-2">Med FRT</th>
              <th className="text-right p-2">P90 FRT</th>
              <th className="text-right p-2">Booked</th>
              <th className="text-right p-2">Lost</th>
              <th className="text-right p-2">Conv %</th>
              <th className="text-right p-2">Pot. Rev. Lost</th>
              <th className="text-right p-2">Handling</th>
              <th className="text-right p-2">RAG</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const m = r.metrics; const s = r.handling_score;
              const rag = r.rag === "RED" ? "bg-red-100 text-red-800"
                : r.rag === "AMBER" ? "bg-amber-100 text-amber-800"
                : "bg-emerald-100 text-emerald-800";
              return (
                <tr key={r.center} className="border-b hover:bg-muted/30 cursor-pointer"
                  onClick={() => onPickCenter?.(r.center)}
                  data-testid={`wi-cmp-row-${r.center}`}>
                  <td className="p-2 font-semibold">{r.center}</td>
                  <td className="p-2 text-right">{r.currency}</td>
                  <td className="p-2 text-right">{m.total_enquiries}</td>
                  <td className="p-2 text-right">{m.valid_sales_leads}</td>
                  <td className="p-2 text-right">{m.answered}</td>
                  <td className={`p-2 text-right ${m.unanswered ? "text-red-700 font-semibold" : ""}`}>
                    {m.unanswered}</td>
                  <td className="p-2 text-right">{m.median_first_response_minutes ?? "—"} m</td>
                  <td className="p-2 text-right">{m.p90_first_response_minutes ?? "—"} m</td>
                  <td className="p-2 text-right">{m.booked_or_ordered}</td>
                  <td className="p-2 text-right">{m.lost}</td>
                  <td className="p-2 text-right"><strong>{m.conversion_pct}%</strong></td>
                  <td className="p-2 text-right">{_FMT(m.potential_revenue_lost, r.symbol)}</td>
                  <td className="p-2 text-right">{s.score} / 100</td>
                  <td className={`p-2 text-right`}><Badge className={rag}>{r.rag}</Badge></td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={14} className="p-6 text-center text-slate-500">
                No data — try seeding demo data.
              </td></tr>
            )}
          </tbody>
        </table>
        <div className="text-[10px] text-slate-500 italic mt-2">
          Mixed-currency totals are intentionally NOT summed. Each centre uses its
          own currency until a consolidated FX feature is approved.
        </div>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
// Inbox Tab — 3-panel with filters + search
// ─────────────────────────────────────────────────────────────
function InboxTab({ session, filters, currencySymbol, onDashboardRefresh }) {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [openConv, setOpenConv] = useState(null);
  const [quick, setQuick] = useState("");
  const [sort, setSort] = useState("newest");
  const [status, setStatus] = useState("");
  const [category, setCategory] = useState("");
  const [customerQ, setCustomerQ] = useState("");
  const [messageQ, setMessageQ] = useState("");
  const [bookingId, setBookingId] = useState("");

  const search = async () => {
    try {
      const r = await api.post("/whatsapp-intel/inbox/search", {
        token: session.token,
        center: filters.center || null,
        date_from: filters.dateFrom || null,
        date_to: filters.dateTo || null,
        include_demo: filters.includeDemo,
        status: status || null, category: category || null,
        customer_query: customerQ || null, message_query: messageQ || null,
        booking_id: bookingId || null,
        quick: quick || null, sort, limit: 200,
      });
      setRows(r.data.rows); setCount(r.data.count);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { search(); /* eslint-disable-line */ }, [
    filters.center, filters.dateFrom, filters.dateTo, filters.includeDemo,
    status, category, quick, sort]);

  const openConversation = async (conv_id) => {
    try {
      const r = await api.post(
        `/whatsapp-intel/inbox/conversation/${conv_id}`,
        { token: session.token });
      setOpenConv(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center gap-2"><Filter className="w-4 h-4" />
            Central Inbox — {count} conversation{count === 1 ? "" : "s"}</span>
          {openConv && (
            <Button size="sm" variant="ghost" onClick={() => setOpenConv(null)}>
              Close conversation
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {/* Filters */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-xs">
          <Select value={quick || "ALL"} onValueChange={(v) => setQuick(v === "ALL" ? "" : v)}>
            <SelectTrigger data-testid="wi-inbox-quick"><SelectValue placeholder="Quick filter" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All (no quick)</SelectItem>
              <SelectItem value="today">Today's Enquiries</SelectItem>
              <SelectItem value="yesterday">Yesterday</SelectItem>
              <SelectItem value="unanswered">Unanswered</SelectItem>
              <SelectItem value="follow_up">Needs Follow-up</SelectItem>
              <SelectItem value="possible_match">Possible Booking Match</SelectItem>
              <SelectItem value="lost">Lost</SelectItem>
              <SelectItem value="unresolved">Unresolved</SelectItem>
              <SelectItem value="converted">Converted</SelectItem>
              <SelectItem value="complaints">Complaints</SelectItem>
            </SelectContent>
          </Select>
          <Select value={status || "ALL"} onValueChange={(v) => setStatus(v === "ALL" ? "" : v)}>
            <SelectTrigger data-testid="wi-inbox-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Any status</SelectItem>
              {STATUSES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={category || "ALL"} onValueChange={(v) => setCategory(v === "ALL" ? "" : v)}>
            <SelectTrigger data-testid="wi-inbox-category"><SelectValue placeholder="Category" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Any category</SelectItem>
              {CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="wi-inbox-sort"><SelectValue placeholder="Sort" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="oldest">Oldest</SelectItem>
              <SelectItem value="longest_unans">Longest Unanswered</SelectItem>
              <SelectItem value="highest_value">Highest Potential Value</SelectItem>
              <SelectItem value="lowest_score">Lowest Handling Score</SelectItem>
            </SelectContent>
          </Select>
          <Input placeholder="Search customer (name / mobile)"
            value={customerQ} onChange={(e) => setCustomerQ(e.target.value)}
            onBlur={search} data-testid="wi-inbox-customer-search" />
          <Input placeholder="Search message text"
            value={messageQ} onChange={(e) => setMessageQ(e.target.value)}
            onBlur={search} data-testid="wi-inbox-message-search" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-xs">
          <Input placeholder="Booking ID" value={bookingId}
            onChange={(e) => setBookingId(e.target.value)} onBlur={search}
            data-testid="wi-inbox-booking-id" />
          <Button size="sm" variant="outline" onClick={search}
            data-testid="wi-inbox-search-btn">
            <Filter className="w-3 h-3 mr-1" />Apply
          </Button>
        </div>

        {/* 3-panel */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 min-h-[500px]">
          <div className="md:col-span-1 border rounded overflow-y-auto max-h-[560px]"
            data-testid="wi-inbox-left">
            {rows.map((l) => (
              <div key={l.conversation_id}
                className={`p-2 border-b cursor-pointer hover:bg-muted/50 ${
                  openConv?.conversation?.conversation_id === l.conversation_id
                    ? "bg-emerald-50" : ""
                }`}
                onClick={() => openConversation(l.conversation_id)}
                data-testid={`wi-conv-row-${l.conversation_id}`}>
                <div className="flex items-center justify-between">
                  <strong className="text-sm">
                    {l.customer?.display_name || l.customer?.mobile_norm || "—"}
                  </strong>
                  <Badge variant="outline" className={_STATUS_BADGE[l.status] || ""}>
                    {l.status}
                  </Badge>
                </div>
                <div className="text-xs text-slate-600">{l.center} · {l.category}</div>
                <div className="text-xs text-slate-500 truncate">{l.last_message_preview}</div>
              </div>
            ))}
            {rows.length === 0 && (
              <div className="p-4 text-center text-slate-500 text-sm">
                No conversations match your filters.
              </div>
            )}
          </div>

          <div className="md:col-span-2 border rounded p-2 overflow-y-auto max-h-[560px]"
            data-testid="wi-inbox-mid">
            {openConv ? (
              <>
                <div className="flex items-center justify-between border-b pb-2 mb-2">
                  <strong>{openConv.customer?.display_name || "—"}</strong>
                  <span className="text-xs text-slate-500">
                    {openConv.conversation?.center} · {openConv.conversation?.message_count} messages
                  </span>
                </div>
                <div className="space-y-2">
                  {openConv.messages.map((m) => (
                    <div key={m.message_id}
                      className={`p-2 rounded max-w-[85%] text-sm ${
                        m.direction === "IN" ? "bg-slate-100 mr-auto" :
                          m.direction === "OUT" ? "bg-emerald-100 ml-auto" :
                            "bg-amber-50 mx-auto italic text-xs"
                      }`}>
                      <div>{m.body}</div>
                      <div className="text-[10px] text-slate-500 mt-1">
                        {new Date(m.timestamp).toLocaleString()}
                        {m.is_automated_ack ? " · Automated Ack" : ""}
                      </div>
                    </div>
                  ))}
                </div>
                {openConv.cross_centre_duplicates?.length > 0 && (
                  <div className="mt-3 p-2 rounded bg-amber-50 border border-amber-300 text-xs">
                    <AlertTriangle className="w-3 h-3 inline mr-1" />
                    <strong>Possible cross-centre enquiry:</strong>{" "}
                    {openConv.cross_centre_duplicates.map((d) => d.center).join(", ")}.
                    Not merged — Management can review.
                  </div>
                )}
              </>
            ) : (
              <div className="text-center text-slate-500 pt-24">
                Select a conversation to see its timeline.
              </div>
            )}
          </div>

          <div className="md:col-span-1 border rounded p-2 space-y-3 overflow-y-auto max-h-[560px]"
            data-testid="wi-inbox-right">
            {openConv?.lead ? (
              <LeadDetails lead={openConv.lead} customer={openConv.customer}
                session={session} currencySymbol={currencySymbol}
                onChanged={() => openConversation(openConv.conversation.conversation_id)}
                onDashboardRefresh={onDashboardRefresh} />
            ) : (
              <div className="text-xs text-slate-500 pt-2">
                Lead details will appear here.
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
// Alerts Tab
// ─────────────────────────────────────────────────────────────
function AlertsTab({ session, filters }) {
  const [data, setData] = useState({ rows: [], count: 0, summary: {} });
  const [status, setStatus] = useState("OPEN");
  const [severity, setSeverity] = useState("");
  const [alertType, setAlertType] = useState("");
  const [thresholds, setThresholds] = useState(null);

  const load = async () => {
    try {
      const r = await api.post("/whatsapp-intel/alerts/list", {
        token: session.token, center: filters.center || null,
        date_from: filters.dateFrom || null, date_to: filters.dateTo || null,
        include_demo: filters.includeDemo,
        status: status || null, severity: severity || null,
        alert_type: alertType || null,
      });
      setData(r.data);
      const t = await api.post("/whatsapp-intel/alerts/thresholds",
        { token: session.token });
      setThresholds(t.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [
    filters.center, filters.dateFrom, filters.dateTo, filters.includeDemo,
    status, severity, alertType]);

  const act = async (endpoint, alert_id, extra = {}) => {
    try {
      await api.post(`/whatsapp-intel/alerts/${endpoint}`, {
        token: session.token, alert_id, ...extra });
      toast.success("Done"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <Tile label="Open" value={data.summary.open || 0} />
        <Tile label="Acknowledged" value={data.summary.acknowledged || 0} />
        <Tile label="Red" value={data.summary.red || 0}
          tone={data.summary.red ? "border-red-300" : ""} />
        <Tile label="Amber" value={data.summary.amber || 0} />
        <Tile label="Resolved" value={data.summary.resolved || 0} />
        <Tile label="Dismissed" value={data.summary.dismissed || 0} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
        <Select value={status || "ALL"} onValueChange={(v) => setStatus(v === "ALL" ? "" : v)}>
          <SelectTrigger data-testid="wi-alert-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All</SelectItem>
            {["OPEN","ACKNOWLEDGED","RESOLVED","DISMISSED"].map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={severity || "ALL"} onValueChange={(v) => setSeverity(v === "ALL" ? "" : v)}>
          <SelectTrigger data-testid="wi-alert-severity"><SelectValue placeholder="Severity" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All</SelectItem>
            {["RED","AMBER","INFO"].map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={alertType || "ALL"} onValueChange={(v) => setAlertType(v === "ALL" ? "" : v)}>
          <SelectTrigger data-testid="wi-alert-type"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All</SelectItem>
            {(thresholds?.alert_types || []).map((t) => (
              <SelectItem key={t} value={t}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Card><CardContent className="overflow-x-auto p-0">
        <table className="w-full text-sm">
          <thead><tr className="bg-muted/50 border-b">
            <th className="text-left p-2">Type</th>
            <th className="text-left p-2">Sev.</th>
            <th className="text-left p-2">Centre</th>
            <th className="text-left p-2">Reason</th>
            <th className="text-left p-2">Status</th>
            <th className="text-left p-2">Created</th>
            <th className="text-left p-2">Actions</th>
          </tr></thead>
          <tbody>
            {data.rows.map((a) => (
              <tr key={a.alert_id} className="border-b"
                data-testid={`wi-alert-row-${a.alert_id}`}>
                <td className="p-2 whitespace-nowrap">{a.alert_type}</td>
                <td className="p-2"><Badge className={_SEV_BADGE[a.severity]}>{a.severity}</Badge></td>
                <td className="p-2">{a.center}</td>
                <td className="p-2 text-xs">{a.reason}</td>
                <td className="p-2">{a.status}</td>
                <td className="p-2 text-xs">{a.created_at?.slice(0,16).replace("T"," ")}</td>
                <td className="p-2 space-x-1">
                  {a.status === "OPEN" && (
                    <Button size="sm" variant="outline"
                      onClick={() => act("acknowledge", a.alert_id)}
                      data-testid={`wi-alert-ack-${a.alert_id}`}>Ack</Button>
                  )}
                  {a.status !== "RESOLVED" && a.status !== "DISMISSED" && (
                    <>
                      <Button size="sm" variant="outline"
                        onClick={() => {
                          const note = window.prompt("Resolution note (required):");
                          if (note && note.trim())
                            act("resolve", a.alert_id, { resolution_note: note });
                        }}>Resolve</Button>
                      <Button size="sm" variant="outline"
                        onClick={() => {
                          const reason = window.prompt("Dismiss reason (required):");
                          if (reason && reason.trim())
                            act("dismiss", a.alert_id, { reason });
                        }}>Dismiss</Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
            {data.rows.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-slate-500">
                No alerts for these filters.
              </td></tr>
            )}
          </tbody>
        </table>
      </CardContent></Card>
      {thresholds && (
        <details className="text-xs">
          <summary className="cursor-pointer text-slate-600">
            Active thresholds (edit via <code>whatsapp_settings.alert_thresholds</code>)
          </summary>
          <pre className="bg-slate-50 p-2 rounded border overflow-x-auto">
            {JSON.stringify(thresholds.thresholds, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Reports Tab
// ─────────────────────────────────────────────────────────────
function ReportsTab({ session, filters }) {
  const [busy, setBusy] = useState("");
  const download = async (fmt) => {
    setBusy(fmt);
    try {
      const r = await api.post(`/whatsapp-intel/reports/${fmt}`, {
        token: session.token, center: filters.center || null,
        date_from: filters.dateFrom || null, date_to: filters.dateTo || null,
        include_demo: filters.includeDemo,
      }, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `WhatsApp_Intelligence_${filters.center || "ALL"}` +
        `_${filters.dateFrom || "all"}_${filters.dateTo || "all"}.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      a.click(); URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed");
    } finally { setBusy(""); }
  };

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">
        Purnabramha — WhatsApp Sales Intelligence Report</CardTitle></CardHeader>
      <CardContent className="space-y-2 text-sm">
        <p>Use the same styling as other Purnabramha management reports.
          The report always uses the canonical calculation engine.
        </p>
        <div className="flex gap-2">
          <Button onClick={() => download("pdf")} disabled={!!busy}
            data-testid="wi-report-pdf">
            <Download className="w-4 h-4 mr-1" />
            {busy === "pdf" ? "Building…" : "Download PDF"}
          </Button>
          <Button onClick={() => download("excel")} disabled={!!busy}
            variant="outline" data-testid="wi-report-excel">
            <Download className="w-4 h-4 mr-1" />
            {busy === "excel" ? "Building…" : "Download Excel"}
          </Button>
        </div>
        <p className="text-[10px] text-slate-500 italic">
          Includes: Executive Summary · Sales Funnel · Centre Comparison ·
          Lead Category Breakdown · Response Performance · Unanswered
          Opportunities · Lost + Reasons · Unresolved · Potential Revenue
          Lost · Booking Match Summary · Handling Score Breakdown ·
          Alerts Summary · Management Observations.
        </p>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
// Integration Tab — simplified Management view + Advanced (SA) setup
// ─────────────────────────────────────────────────────────────
function IntegrationTab({ session, isAdmin }) {
  const [rows, setRows] = useState([]);
  const [health, setHealth] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saveState, setSaveState] = useState({ status: "idle", at: null, msg: "" });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [testing, setTesting] = useState("");
  const [rotatedToken, setRotatedToken] = useState(null); // shown ONCE
  const [monitorCentre, setMonitorCentre] = useState("");
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [mockEnabled, setMockEnabled] = useState(false);
  const [mockScenario, setMockScenario] = useState("");

  const load = async () => {
    try {
      const [r, h] = await Promise.all([
        api.post("/whatsapp-intel/integration/list", { token: session.token }),
        api.post("/whatsapp-intel/pilot/health", { token: session.token }),
      ]);
      setRows(r.data.rows || []);
      setMockEnabled(!!r.data.mock_enabled);
      setHealth(h.data.rows || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const runDiagnostics = async (centre) => {
    if (!centre) return;
    setDiagLoading(true);
    try {
      const r = await api.post("/whatsapp-intel/pilot/diagnostics",
        { token: session.token, center: centre, mock_scenario: mockScenario || null });
      setDiag(r.data);
      setMonitorCentre(centre);
    } catch (e) {
      toast.error(`Diagnostics failed: ${e.response?.data?.detail || e.message}`);
    } finally { setDiagLoading(false); }
  };

  // Auto-select the first configured centre with credentials for the monitor.
  useEffect(() => {
    if (monitorCentre || rows.length === 0) return;
    const preferred = rows.find((r) => r.waba_id_status === "CONFIGURED")
                      || rows[0];
    if (preferred) runDiagnostics(preferred.center);
  }, [rows]); // eslint-disable-line

  const healthOf = (code) => health.find((x) => x.center === code) || {};

  const connectionStatus = (row) => {
    const h = healthOf(row.center);
    const ps = h.pilot_status || row.integration_status || "NOT_CONFIGURED";
    const webhookVerified = (row.webhook_status || "").toUpperCase() === "VERIFIED";
    const inboundOn = !!h.live_inbound_enabled;
    const hasRealMessage = !!h.last_message_at;
    // iter277 — "CONNECTED" is ONLY shown when Meta's subscription
    // challenge succeeded, live inbound is enabled AND at least one
    // real inbound event landed.  Passing the challenge is not proof
    // that data flows.
    if ((ps === "LIVE" || ps === "LIVE_PILOT") && webhookVerified && inboundOn && hasRealMessage)
      return { label: "CONNECTED", tone: "bg-emerald-100 text-emerald-800 border-emerald-300" };
    if ((ps === "LIVE" || ps === "LIVE_PILOT") && !webhookVerified)
      return { label: "WEBHOOK NOT VERIFIED", tone: "bg-red-100 text-red-800 border-red-300" };
    if ((ps === "LIVE" || ps === "LIVE_PILOT") && webhookVerified && !hasRealMessage)
      return { label: "WAITING FOR FIRST LIVE MESSAGE",
                tone: "bg-amber-100 text-amber-900 border-amber-300" };
    if (ps === "READY_FOR_PILOT" && webhookVerified)
      return { label: "READY — awaiting SA activation", tone: "bg-sky-100 text-sky-800 border-sky-300" };
    if (ps === "PAUSED")
      return { label: "PAUSED", tone: "bg-amber-100 text-amber-800 border-amber-300" };
    if (ps === "ERROR")
      return { label: "ERROR", tone: "bg-red-100 text-red-800 border-red-300" };
    if (ps === "READY_FOR_INBOUND_TEST" || (row.waba_id_status === "CONFIGURED" && !webhookVerified))
      return { label: "WAITING FOR FIRST LIVE MESSAGE",
                tone: "bg-amber-100 text-amber-900 border-amber-300" };
    if (ps === "READY_FOR_CREDENTIALS" || ps === "VALIDATING")
      return { label: "ACTION REQUIRED — add Access Token & App Secret",
                tone: "bg-amber-100 text-amber-900 border-amber-300" };
    return { label: "NOT CONFIGURED",
              tone: "bg-slate-100 text-slate-700 border-slate-300" };
  };

  const openEdit = (r) => {
    setEditing({
      center: r.center,
      official_whatsapp_number: r.official_whatsapp_number || "",
      display_name: r.display_name || "",
      provider: r.provider || "META_CLOUD",
      integration_status: r.integration_status || "NOT_CONFIGURED",
      enabled: r.enabled ?? false,
      notes: r.notes || "",
      meta_app_id: r.meta_app_id || "",
    });
    setSaveState({ status: "idle", at: null, msg: "" });
    setShowAdvanced(false);
  };

  const save = async () => {
    setSaveState({ status: "saving", at: null, msg: "" });
    try {
      // Do NOT send empty-string secrets — the backend treats those as
      // "leave unchanged" already, but sending them wastes an encrypt round.
      const payload = { token: session.token, ...editing };
      for (const k of ["waba_id","phone_number_id","access_token",
                          "app_secret","webhook_verify_token"]) {
        if (!payload[k]) delete payload[k];
      }
      await api.post("/whatsapp-intel/integration/save", payload);
      const now = new Date().toLocaleTimeString();
      setSaveState({ status: "saved", at: now, msg: "" });
      // Re-fetch and pre-fill the editing panel from the server — this
      // proves persistence visibly.  We DO NOT close the panel.
      await load();
      // Refresh editing state with just-persisted values.
      const rNow = (await api.post("/whatsapp-intel/integration/list",
                        { token: session.token })).data.rows;
      const fresh = (rNow || []).find((x) => x.center === editing.center);
      if (fresh) {
        setEditing((s) => ({ ...s,
          official_whatsapp_number: fresh.official_whatsapp_number || "",
          display_name: fresh.display_name || "",
          provider: fresh.provider || "META_CLOUD",
          integration_status: fresh.integration_status,
          enabled: fresh.enabled,
          notes: fresh.notes || "",
          meta_app_id: fresh.meta_app_id || "",
          // Clear only the write-only credential inputs.
          waba_id: "", phone_number_id: "", access_token: "",
          app_secret: "", webhook_verify_token: "",
        }));
      }
      toast.success(`Saved ✓  ${now}`);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || "unknown error";
      setSaveState({ status: "error", at: null, msg });
      toast.error(`Save failed — ${msg}`);
    }
  };

  const connectWhatsApp = async (row) => {
    setTesting(`connect:${row.center}`);
    try {
      const r = await api.post("/whatsapp-intel/pilot/validate-credentials",
        { token: session.token, center: row.center });
      const missing = r.data.missing_fields || [];
      if (missing.length === 0) {
        toast.success(`CONNECTED ✓  — ${row.center} ready for SA activation.`);
      } else {
        toast.error(
          `ACTION REQUIRED — ${row.center} missing: ${missing.join(", ")}.  ` +
          `Open Advanced Technical Setup below to add them.`);
      }
      await load();
    } catch (e) {
      toast.error(`ERROR — ${e.response?.data?.detail || e.message}`);
    } finally { setTesting(""); }
  };

  const testConnection = async (row) => {
    setTesting(`test:${row.center}`);
    try {
      const r = await api.post("/whatsapp-intel/pilot/validate-credentials",
        { token: session.token, center: row.center });
      const missing = r.data.missing_fields || [];
      const mapping = r.data.phone_number_mapping_present;
      const lines = [
        `Centre: ${row.center}`,
        `Status: ${r.data.pilot_status}`,
        `Phone-Number-ID routing: ${mapping ? "OK" : "missing"}`,
        `Credentials configured: ${(r.data.configured_fields || []).join(", ") || "—"}`,
        `Missing: ${missing.join(", ") || "—"}`,
      ];
      window.alert("Test Connection\n\n" + lines.join("\n"));
    } catch (e) {
      window.alert(`Test failed:\n${e.response?.data?.detail || e.message}`);
    } finally { setTesting(""); }
  };

  const pause = async (row) => {
    if (!window.confirm(
      `Pause WhatsApp integration for ${row.center}?  History is preserved; no inbound events are stored while paused.`)) return;
    try {
      await api.post("/whatsapp-intel/pilot/pause",
        { token: session.token, center: row.center });
      toast.success(`Paused — ${row.center}`); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const rotateVerifyToken = async () => {
    if (!editing?.center) return;
    if (!window.confirm(
      `Generate a new Webhook Verify Token for ${editing.center}?  ` +
      `The value is shown once for you to copy into Meta. It will not be ` +
      `retrievable afterwards. Generating does NOT enable inbound/outbound ` +
      `and does NOT mark the webhook VERIFIED.`)) return;
    setTesting("rotate");
    try {
      const r = await api.post("/whatsapp-intel/integration/rotate-verify-token",
        { token: session.token, center: editing.center });
      setRotatedToken({ token: r.data.verify_token,
                         rotated_at: r.data.rotated_at });
      toast.success("Verify Token generated");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setTesting(""); }
  };

  const testWebhook = async () => {
    if (!editing?.center) return;
    setTesting("test-webhook");
    try {
      const r = await api.post("/whatsapp-intel/integration/test-webhook",
        { token: session.token, center: editing.center });
      const d = r.data;
      window.alert(
        `Result: ${d.result}\n` +
        `HTTP: ${d.http_status ?? "—"}\n\n` +
        `${d.message}\n\n` +
        `Callback URL tested:\n${d.callback_url}\n\n` +
        d.note);
    } catch (e) {
      window.alert(`Test Webhook failed:\n${e.response?.data?.detail || e.message}`);
    } finally { setTesting(""); }
  };

  // Server-resolved production URL (never guessed).
  const callbackUrl = (rows[0]?.callback_url)
                       || (editing && rows.find(r => r.center === editing.center)?.callback_url)
                       || "";

  return (
    <Card>
      <CardHeader><CardTitle className="text-base flex items-center gap-2">
        <Settings2 className="w-4 h-4" />
        WhatsApp Integration Settings
        <Badge className="bg-amber-100 text-amber-900 border-amber-300 ml-2">
          META CONNECTION: NOT ACTIVE
        </Badge>
      </CardTitle></CardHeader>
      <CardContent className="overflow-x-auto space-y-3">
        {/* iter277 — LIVE WEBHOOK MONITOR */}
        <MetaDiagnosticsPanel
          session={session}
          mockEnabled={mockEnabled} scenario={mockScenario} setScenario={setMockScenario}
          diag={diag} loading={diagLoading}
          centreOptions={rows.filter(r => r.waba_id_status === "CONFIGURED")}
          activeCentre={monitorCentre}
          onPick={(c) => runDiagnostics(c)}
          onRefresh={() => runDiagnostics(monitorCentre)} />

        {/* Normal Management view */}
        <table className="w-full text-sm">
          <thead><tr className="bg-muted/50 border-b">
            <th className="text-left p-2">Centre</th>
            <th className="text-left p-2">Official WhatsApp #</th>
            <th className="text-left p-2">Display Name</th>
            <th className="text-left p-2">Connection Status</th>
            <th className="text-left p-2">Inbound</th>
            <th className="text-left p-2">Outbound</th>
            <th className="text-left p-2">Last Message</th>
            <th className="text-left p-2 w-64">Actions</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => {
              const h = healthOf(r.center);
              const cs = connectionStatus(r);
              return (
                <tr key={r.center} className="border-b"
                  data-testid={`wi-integration-row-${r.center}`}>
                  <td className="p-2 font-semibold" data-testid={`wi-int-center-${r.center}`}>
                    {r.center}
                  </td>
                  <td className="p-2" data-testid={`wi-int-number-${r.center}`}>
                    {r.official_whatsapp_number || <span className="text-slate-400">—</span>}
                  </td>
                  <td className="p-2" data-testid={`wi-int-display-${r.center}`}>
                    {r.display_name || <span className="text-slate-400">—</span>}
                  </td>
                  <td className="p-2"><Badge className={cs.tone}>{cs.label}</Badge></td>
                  <td className={`p-2 ${h.live_inbound_enabled ? "text-emerald-700 font-semibold" : "text-slate-500"}`}>
                    {h.live_inbound_enabled ? "ON" : "OFF"}
                  </td>
                  <td className={`p-2 ${h.live_outbound_enabled ? "text-emerald-700 font-semibold" : "text-slate-500"}`}>
                    {h.live_outbound_enabled ? "ON" : "OFF"}
                  </td>
                  <td className="p-2 text-xs text-slate-500">
                    {h.last_message_at ? h.last_message_at.slice(0,16).replace("T"," ") : "—"}
                  </td>
                  <td className="p-2 space-x-1 whitespace-nowrap">
                    {isAdmin && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => openEdit(r)}
                          data-testid={`wi-integration-edit-${r.center}`}>Edit</Button>
                        <Button size="sm" onClick={() => connectWhatsApp(r)}
                          disabled={testing === `connect:${r.center}`}
                          className="bg-emerald-600"
                          data-testid={`wi-integration-connect-${r.center}`}>
                          {testing === `connect:${r.center}` ? "Checking…" : "Connect WhatsApp"}
                        </Button>
                        <Button size="sm" variant="outline"
                          onClick={() => testConnection(r)}
                          disabled={testing === `test:${r.center}`}
                          data-testid={`wi-integration-test-${r.center}`}>
                          {testing === `test:${r.center}` ? "Testing…" : "Test Connection"}
                        </Button>
                        {(h.pilot_status === "LIVE_PILOT" || h.pilot_status === "LIVE") && (
                          <Button size="sm" variant="outline" onClick={() => pause(r)}
                            data-testid={`wi-integration-pause-${r.center}`}>Pause</Button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr><td colSpan={8} className="p-6 text-center text-slate-500">
                No centres available yet.
              </td></tr>
            )}
          </tbody>
        </table>

        <div className="text-[11px] text-slate-500 italic">
          Normal management workflow: <strong>Select Centre → Enter WhatsApp Number →
          Save → Connect WhatsApp → Test → Start Live Inbound.</strong> Every save is
          confirmed with <em>Saved ✓</em> and refreshed from the server so persistence is
          visible.  Access Token / App Secret / Verify Token stay under Advanced
          Technical Setup (SA only) and are stored encrypted server-side.
        </div>

        {/* Edit panel */}
        {editing && (
          <Card className="border-emerald-300">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-sm">Edit — {editing.center}</CardTitle>
              <div className="flex items-center gap-2 text-xs">
                {saveState.status === "saving" && (
                  <Badge className="bg-slate-100 text-slate-700 border-slate-300"
                          data-testid="wi-save-state-saving">
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Saving…
                  </Badge>
                )}
                {saveState.status === "saved" && (
                  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300"
                          data-testid="wi-save-state-saved">
                    Saved ✓  {saveState.at}
                  </Badge>
                )}
                {saveState.status === "error" && (
                  <Badge className="bg-red-100 text-red-800 border-red-300"
                          data-testid="wi-save-state-error">
                    Save failed — {saveState.msg}
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              <Label>Official WhatsApp number (E.164, e.g. +919...)</Label>
              <Input value={editing.official_whatsapp_number || ""}
                onChange={(e) => setEditing((s) => ({ ...s, official_whatsapp_number: e.target.value }))}
                data-testid="wi-integration-number" />
              <Label>Display name</Label>
              <Input value={editing.display_name || ""}
                onChange={(e) => setEditing((s) => ({ ...s, display_name: e.target.value }))}
                data-testid="wi-integration-display-name" />
              <Label>Notes</Label>
              <Input value={editing.notes || ""}
                onChange={(e) => setEditing((s) => ({ ...s, notes: e.target.value }))}
                data-testid="wi-integration-notes" />
              <Label>Enabled</Label>
              <input type="checkbox" checked={!!editing.enabled}
                onChange={(e) => setEditing((s) => ({ ...s, enabled: e.target.checked }))}
                data-testid="wi-integration-enabled" />

              <div className="col-span-2 flex gap-2 justify-end pt-2 border-t">
                <Button variant="ghost" onClick={() => setEditing(null)}
                  data-testid="wi-integration-close">Close</Button>
                <Button onClick={save} disabled={saveState.status === "saving"}
                  className="bg-emerald-600"
                  data-testid="wi-integration-save">
                  {saveState.status === "saving" ? "Saving…" : "Save"}
                </Button>
              </div>

              {/* Advanced Technical Setup — SA/Technical only */}
              <div className="col-span-2 mt-2">
                <button className="text-xs text-slate-600 underline"
                  onClick={() => setShowAdvanced((v) => !v)}
                  data-testid="wi-integration-advanced-toggle">
                  {showAdvanced ? "▾" : "▸"} Advanced Technical Setup — SA / Technical only
                </button>
              </div>
              {showAdvanced && (
                <>
                  <div className="col-span-2 rounded border bg-slate-50 p-2 text-[11px] text-slate-600">
                    Secrets are write-only.  On save they are encrypted with Fernet
                    and stored server-side.  They are never returned via any GET
                    endpoint, log line, audit trail, PDF or Excel.  Leave a field
                    blank to keep its previously-saved value.
                  </div>
                  <Label>Production Webhook Callback URL</Label>
                  <div className="flex items-center gap-1">
                    <Input readOnly value={callbackUrl}
                      data-testid="wi-integration-callback-url" />
                    <Button size="sm" variant="outline"
                      onClick={() => {
                        navigator.clipboard?.writeText(callbackUrl);
                        toast.success("Callback URL copied");
                      }}
                      data-testid="wi-integration-copy-url"
                      disabled={!callbackUrl}>
                      Copy URL
                    </Button>
                  </div>

                  <Label>Webhook Verify Token</Label>
                  <div className="flex items-center gap-1">
                    <Input readOnly
                      value={editing.center && rows.find(r => r.center === editing.center)?.webhook_verify_token_status === "CONFIGURED"
                              ? "Configured ✓ — never displayed again"
                              : "Not configured"}
                      data-testid="wi-integration-verify-status" />
                    <Button size="sm"
                      onClick={rotateVerifyToken}
                      disabled={testing === "rotate"}
                      data-testid="wi-integration-generate-token">
                      {testing === "rotate" ? "Generating…" : "Generate Verify Token"}
                    </Button>
                    <Button size="sm" variant="outline"
                      onClick={testWebhook}
                      disabled={testing === "test-webhook" || !callbackUrl}
                      data-testid="wi-integration-test-webhook">
                      {testing === "test-webhook" ? "Testing…" : "Test Webhook"}
                    </Button>
                  </div>
                  {rotatedToken && (
                    <div className="col-span-2 rounded border-2 border-amber-400 bg-amber-50 p-3 space-y-2"
                          data-testid="wi-verify-token-disclosure">
                      <div className="text-xs font-bold text-amber-900">
                        ⚠️  Copy this token now — it will not be shown again.
                      </div>
                      <div className="flex items-center gap-1">
                        <Input readOnly value={rotatedToken.token}
                          className="font-mono text-xs"
                          data-testid="wi-verify-token-value" />
                        <Button size="sm"
                          onClick={() => {
                            navigator.clipboard?.writeText(rotatedToken.token);
                            toast.success("Verify Token copied");
                          }}
                          data-testid="wi-verify-token-copy">
                          Copy Token
                        </Button>
                        <Button size="sm" variant="outline"
                          onClick={() => setRotatedToken(null)}
                          data-testid="wi-verify-token-close">
                          Close
                        </Button>
                      </div>
                      <div className="text-[10px] text-amber-900 italic">
                        Rotated at {rotatedToken.rotated_at}. Generation does
                        NOT verify the webhook, does NOT enable inbound/outbound.
                        Paste this into Meta → Webhooks, then wait for Meta's
                        actual subscription verification.
                      </div>
                    </div>
                  )}

                  <Label>Provider</Label>
                  <Select value={editing.provider || "META_CLOUD"}
                    onValueChange={(v) => setEditing((s) => ({ ...s, provider: v }))}>
                    <SelectTrigger data-testid="wi-integration-provider">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="META_CLOUD">Meta Cloud (direct)</SelectItem>
                      <SelectItem value="BSP">Meta Business Solution Provider</SelectItem>
                    </SelectContent>
                  </Select>
                  <Label>Integration Status</Label>
                  <Select value={editing.integration_status || "NOT_CONFIGURED"}
                    onValueChange={(v) => setEditing((s) => ({ ...s, integration_status: v }))}>
                    <SelectTrigger data-testid="wi-integration-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {["NOT_CONFIGURED","READY_FOR_CREDENTIALS","READY_FOR_PILOT",
                        "DISABLED","ERROR"].map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Label>Intended Meta App ID</Label>
                  <Input placeholder="Numeric App ID (not secret)" value={editing.meta_app_id || ""}
                    onChange={(e) => setEditing((s) => ({ ...s, meta_app_id: e.target.value }))}
                    data-testid="wi-integration-meta-app-id" />
                  <Label>WABA ID</Label>
                  <Input placeholder="Leave blank to keep existing"
                    onChange={(e) => setEditing((s) => ({ ...s, waba_id: e.target.value }))}
                    data-testid="wi-integration-waba-id" />
                  <Label>Phone Number ID</Label>
                  <Input placeholder="Leave blank to keep existing"
                    onChange={(e) => setEditing((s) => ({ ...s, phone_number_id: e.target.value }))}
                    data-testid="wi-integration-phone-number-id" />
                  <Label>Access Token</Label>
                  <Input type="password" placeholder="Leave blank to keep existing"
                    onChange={(e) => setEditing((s) => ({ ...s, access_token: e.target.value }))}
                    data-testid="wi-integration-access-token" />
                  <Label>App Secret</Label>
                  <Input type="password" placeholder="Leave blank to keep existing"
                    onChange={(e) => setEditing((s) => ({ ...s, app_secret: e.target.value }))}
                    data-testid="wi-integration-app-secret" />
                  <div className="col-span-2 text-[11px] text-slate-500 italic">
                    Note: the Verify Token above is generated for you via the button —
                    do not type it manually.
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────
// Pilot Tab
// ─────────────────────────────────────────────────────────────
function PilotTab({ session, isAdmin }) {
  const [state, setState] = useState(null);
  const [health, setHealth] = useState(null);
  const [rollout, setRollout] = useState([]);
  const load = async () => {
    try {
      const [r, h, ro] = await Promise.all([
        api.post("/whatsapp-intel/pilot/readiness", { token: session.token }),
        api.post("/whatsapp-intel/pilot/health", { token: session.token }),
        api.post("/whatsapp-intel/pilot/rollout-register", { token: session.token }),
      ]);
      setState(r.data); setHealth(h.data); setRollout(ro.data.rows || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const approve = async () => {
    const notes = window.prompt("Approval notes (optional):", "") || "";
    if (!window.confirm(
      "Confirm Management approval for the WhatsApp Pilot?\n\n" +
      "This records approval only.  It will NOT connect Meta, activate any " +
      "WhatsApp number, or start customer messaging."
    )) return;
    try {
      await api.post("/whatsapp-intel/pilot/approve",
        { token: session.token, notes });
      toast.success("Management approval recorded"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const revoke = async () => {
    const notes = window.prompt("Revoke reason:", "") || "";
    if (!notes.trim()) return;
    try {
      await api.post("/whatsapp-intel/pilot/revoke",
        { token: session.token, notes });
      toast.success("Approval revoked"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const doAction = async (endpoint, body, msg) => {
    try {
      await api.post(`/whatsapp-intel/${endpoint}`, { token: session.token, ...body });
      toast.success(msg); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const approveRollout = async () => {
    const notes = window.prompt("Notes for multi-centre rollout approval:", "") || "";
    if (!window.confirm(
      "Approve for MULTI-CENTRE rollout?\n\n" +
      "This unlocks additional centres to enter LIVE_PILOT one-by-one.  " +
      "It does NOT connect any live centre automatically."
    )) return;
    doAction("pilot/approve-rollout", { notes }, "Rollout approved");
  };
  const toggleKill = async () => {
    if (!window.confirm(
      "Toggle the GLOBAL OUTBOUND KILL SWITCH?\n\n" +
      "When ON, no outbound WhatsApp message can be sent from any centre. " +
      "History remains untouched.")) return;
    doAction("pilot/global-outbound-kill-switch",
      { enabled: !(health?.global_flags?.outbound_kill_switch) },
      "Kill switch toggled");
  };

  if (!state || !health) return <div className="p-4 text-slate-500">Loading…</div>;
  const approved = !!state.approval?.approved_at;

  return (
    <div className="space-y-4">
      {/* System banner */}
      <Card className="border-emerald-300">
        <CardHeader><CardTitle className="text-base flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-emerald-700" />
          System Status
        </CardTitle></CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <div className="rounded border p-3 bg-slate-50">
              <div className="text-xs text-slate-500 uppercase">System</div>
              <div className="text-lg font-semibold">{health.system_status}</div>
            </div>
            <div className="rounded border p-3 bg-slate-50">
              <div className="text-xs text-slate-500 uppercase">Live Pilot Enabled</div>
              <div className="text-lg font-semibold">
                {health.global_flags.live_pilot_enabled ? "YES" : "NO"}
              </div>
            </div>
            <div className={`rounded border p-3 ${health.global_flags.outbound_kill_switch ? "bg-red-50 border-red-400" : "bg-slate-50"}`}>
              <div className="text-xs text-slate-500 uppercase">Outbound Kill Switch</div>
              <div className={`text-lg font-semibold ${health.global_flags.outbound_kill_switch ? "text-red-800" : ""}`}>
                {health.global_flags.outbound_kill_switch ? "ON — outbound BLOCKED" : "OFF"}
              </div>
              {isAdmin && (
                <Button size="sm" variant="outline" className="mt-1"
                  onClick={toggleKill} data-testid="wi-kill-switch">
                  Toggle
                </Button>
              )}
            </div>
          </div>
          <div className="text-xs text-slate-600 italic">
            Meta Cloud API is only reachable after SA runs
            <code className="mx-1">/pilot/validate-credentials</code> →
            <code className="mx-1">/pilot/activate-live-pilot</code>. Saving
            credentials alone does not connect Meta.
          </div>
        </CardContent>
      </Card>

      {/* Readiness checklist */}
      <Card>
        <CardHeader><CardTitle className="text-base">Pilot Readiness</CardTitle></CardHeader>
        <CardContent className="space-y-3 text-sm">
          <table className="w-full text-sm">
            <thead><tr className="bg-muted/50 border-b">
              <th className="text-left p-2">Check</th>
              <th className="text-left p-2">Status</th>
              <th className="text-left p-2">Detail</th>
            </tr></thead>
            <tbody>
              {state.checks.map((c) => (
                <tr key={c.name} className="border-b"
                  data-testid={`wi-pilot-check-${c.name}`}>
                  <td className="p-2">{c.name.replace(/_/g, " ")}</td>
                  <td className="p-2">
                    <Badge className={
                      c.status === "PASS" || c.status === "APPROVED"
                        ? "bg-emerald-100 text-emerald-800" :
                      c.status === "FAIL" ? "bg-red-100 text-red-800" :
                      "bg-amber-100 text-amber-800"}>{c.status}</Badge>
                  </td>
                  <td className="p-2 text-xs text-slate-600">{c.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {isAdmin && (
            <div className="flex gap-2 flex-wrap">
              {!approved ? (
                <Button onClick={approve} className="bg-emerald-600"
                  data-testid="wi-pilot-approve">
                  <ShieldCheck className="w-4 h-4 mr-1" />
                  Approve for Pilot
                </Button>
              ) : (
                <>
                  <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
                    MANAGEMENT APPROVED FOR PILOT — {state.approval?.approved_by || "-"}
                  </Badge>
                  <Button variant="outline" onClick={revoke}
                    data-testid="wi-pilot-revoke">Revoke</Button>
                </>
              )}
              <Button variant="outline" onClick={approveRollout}
                data-testid="wi-approve-rollout">
                Approve Multi-Centre Rollout
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Live Pilot Health */}
      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2">
          <Activity className="w-4 h-4" />
          Live Pilot Health — per centre
        </CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-muted/50 border-b">
              <th className="text-left p-2">Centre</th>
              <th className="text-left p-2">Number</th>
              <th className="text-left p-2">Status</th>
              <th className="text-right p-2">Inbound</th>
              <th className="text-right p-2">Outbound</th>
              <th className="text-right p-2">Msgs Today</th>
              <th className="text-right p-2">Leads Today</th>
              <th className="text-right p-2">Dupes ✂</th>
              <th className="text-right p-2">DLQ</th>
              <th className="text-left p-2">Last Msg</th>
              <th className="text-left p-2">RAG</th>
              <th className="text-left p-2">Actions</th>
            </tr></thead>
            <tbody>
              {health.rows.map((r) => (
                <tr key={r.center} className="border-b"
                  data-testid={`wi-health-row-${r.center}`}>
                  <td className="p-2 font-semibold">{r.center}</td>
                  <td className="p-2 text-xs">{r.official_whatsapp_number || "—"}</td>
                  <td className="p-2"><Badge variant="outline">{r.pilot_status}</Badge></td>
                  <td className="p-2 text-right">{r.live_inbound_enabled ? "ON" : "off"}</td>
                  <td className={`p-2 text-right ${r.live_outbound_enabled ? "text-emerald-700 font-semibold" : "text-slate-500"}`}>
                    {r.live_outbound_enabled ? "ON" : "off"}
                  </td>
                  <td className="p-2 text-right">{r.messages_today}</td>
                  <td className="p-2 text-right">{r.leads_today}</td>
                  <td className="p-2 text-right">{r.duplicates_prevented_today}</td>
                  <td className={`p-2 text-right ${r.dlq_today ? "text-red-700 font-semibold" : ""}`}>
                    {r.dlq_today}
                  </td>
                  <td className="p-2 text-xs text-slate-500">
                    {r.last_message_at?.slice(0,16).replace("T", " ") || "—"}
                  </td>
                  <td className="p-2">
                    <Badge className={
                      r.rag === "RED" ? "bg-red-100 text-red-800" :
                      r.rag === "AMBER" ? "bg-amber-100 text-amber-800" :
                      "bg-emerald-100 text-emerald-800"}>{r.rag}</Badge>
                  </td>
                  <td className="p-2 space-x-1">
                    {isAdmin && (
                      <>
                        <Button size="sm" variant="outline"
                          onClick={() => doAction("pilot/validate-credentials",
                                                     { center: r.center },
                                                     "Validation complete")}
                          data-testid={`wi-validate-${r.center}`}>
                          Validate
                        </Button>
                        {r.pilot_status === "READY_FOR_PILOT" && (
                          <Button size="sm" onClick={() => doAction(
                                "pilot/activate-live-pilot",
                                { center: r.center },
                                "LIVE_PILOT activated")}
                            className="bg-emerald-600"
                            data-testid={`wi-activate-${r.center}`}>
                            Activate LIVE_PILOT
                          </Button>
                        )}
                        {r.pilot_status === "LIVE_PILOT" && !r.live_outbound_enabled && (
                          <Button size="sm" variant="outline"
                            onClick={() => doAction("pilot/enable-outbound",
                                                       { center: r.center },
                                                       "Outbound enabled")}
                            data-testid={`wi-enable-outbound-${r.center}`}>
                            Enable Outbound
                          </Button>
                        )}
                        {(r.pilot_status === "LIVE_PILOT" || r.pilot_status === "LIVE") && (
                          <Button size="sm" variant="outline"
                            onClick={() => doAction("pilot/pause",
                                                       { center: r.center },
                                                       "Paused")}
                            data-testid={`wi-pause-${r.center}`}>
                            Pause
                          </Button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {health.rows.length === 0 && (
                <tr><td colSpan={12} className="p-6 text-center text-slate-500">
                  No centres configured. Add credentials via the Integration tab
                  to see live-pilot readiness here.
                </td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Centre Rollout Register */}
      <Card>
        <CardHeader><CardTitle className="text-base">
          Centre Rollout Register
        </CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="bg-muted/50 border-b">
              <th className="text-left p-2">Centre</th>
              <th className="text-left p-2">Country</th>
              <th className="text-left p-2">Currency</th>
              <th className="text-left p-2">Configuration</th>
              <th className="text-left p-2">Webhook</th>
              <th className="text-left p-2">Pilot</th>
              <th className="text-left p-2">Inbound</th>
              <th className="text-left p-2">Outbound</th>
            </tr></thead>
            <tbody>
              {rollout.map((r) => {
                const cfg = r.configuration || {};
                const configured = Object.values(cfg).every(v => v === "CONFIGURED");
                return (
                  <tr key={r.center} className="border-b"
                    data-testid={`wi-rollout-row-${r.center}`}>
                    <td className="p-2 font-semibold">{r.center}</td>
                    <td className="p-2">{r.country || "—"}</td>
                    <td className="p-2">{r.currency}</td>
                    <td className="p-2 text-xs">
                      {configured ? "ALL CONFIGURED" : "MISSING"}
                    </td>
                    <td className="p-2 text-xs">{r.webhook_status}</td>
                    <td className="p-2"><Badge variant="outline">{r.pilot_status}</Badge></td>
                    <td className="p-2">{r.live_inbound_enabled ? "ON" : "off"}</td>
                    <td className="p-2">{r.live_outbound_enabled ? "ON" : "off"}</td>
                  </tr>
                );
              })}
              {rollout.length === 0 && (
                <tr><td colSpan={8} className="p-6 text-center text-slate-500">
                  No centres available.
                </td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <div className="text-[11px] text-red-700">
        <strong>LIVE INTEGRATION SAFETY.</strong> Meta Cloud API is only
        active after SA runs Validate → Activate for a specific centre.
        Saving credentials alone does not connect Meta. Outbound remains
        disabled by default. AI never sends autonomously; managers must
        confirm every send.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Lead details (inbox right pane)
// ─────────────────────────────────────────────────────────────
function LeadDetails({ lead, customer, session, currencySymbol, onChanged, onDashboardRefresh }) {
  const [saving, setSaving] = useState(false);
  const [notes, setNotes] = useState(lead.notes || "");
  const [expected, setExpected] = useState(lead.expected_value ?? "");
  const [followDate, setFollowDate] = useState(lead.follow_up?.date || "");
  const [lostReason, setLostReason] = useState("");
  const [lostNotes, setLostNotes] = useState("");

  const update = async (payload) => {
    setSaving(true);
    try {
      await api.post("/whatsapp-intel/lead/update", {
        token: session.token, lead_id: lead.lead_id, ...payload,
      });
      toast.success("Saved");
      onChanged?.(); onDashboardRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail?.message
        || e.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const markLost = async () => {
    if (!lostReason) { toast.error("Pick a lost reason"); return; }
    if (lostReason === "OTHER" && !lostNotes.trim()) {
      toast.error("Notes required for OTHER"); return;
    }
    try {
      await api.post("/whatsapp-intel/lead/mark-lost", {
        token: session.token, lead_id: lead.lead_id,
        lost_reason: lostReason, lost_notes: lostNotes,
      });
      toast.success("Marked LOST");
      onChanged?.(); onDashboardRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const confirmMatch = async () => {
    try {
      await api.post("/whatsapp-intel/lead/confirm-match",
        { token: session.token, lead_id: lead.lead_id });
      toast.success("Match confirmed");
      onChanged?.(); onDashboardRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const rejectMatch = async () => {
    try {
      await api.post("/whatsapp-intel/lead/reject-match",
        { token: session.token, lead_id: lead.lead_id });
      toast.success("Match rejected");
      onChanged?.(); onDashboardRefresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="text-sm space-y-2" data-testid="wi-lead-details">
      <div>
        <strong>{customer?.display_name || "—"}</strong>
        <div className="text-xs text-slate-600">{customer?.mobile_norm}</div>
      </div>
      <div><strong>Centre:</strong> {lead.center} · <strong>Currency:</strong> {lead.currency}</div>
      <div><strong>Category:</strong> {lead.category}
        <Badge variant="outline" className="ml-1 text-[10px]">
          {lead.category_source}{lead.category_confidence ? ` · ${lead.category_confidence}` : ""}
        </Badge>
      </div>
      <div><strong>Status:</strong>{" "}
        <Badge variant="outline" className={_STATUS_BADGE[lead.status]}>{lead.status}</Badge>
      </div>
      <div className="text-xs text-slate-600">FRT: {lead.first_response_minutes ?? "—"} m</div>
      <div className="text-xs text-slate-600">
        Match: <Badge variant="outline">{lead.match?.state || "UNMATCHED"}</Badge>
      </div>

      <hr />
      <Label className="text-xs">Correct category</Label>
      <Select onValueChange={(v) => update({ category: v })} disabled={saving}>
        <SelectTrigger data-testid="wi-lead-category">
          <SelectValue placeholder="Manual correction (locks source)" />
        </SelectTrigger>
        <SelectContent>
          {CATEGORIES.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
        </SelectContent>
      </Select>

      <Label className="text-xs">Change status</Label>
      <Select onValueChange={(v) => update({ status: v })} disabled={saving}>
        <SelectTrigger data-testid="wi-lead-status">
          <SelectValue placeholder="Pick a status" />
        </SelectTrigger>
        <SelectContent>
          {["NEW","REPLIED","INTERESTED","LINK_SENT","FOLLOW_UP","BOOKED",
            "ORDERED","UNRESOLVED","NOT_A_SALES_LEAD"].map((s) => (
            <SelectItem key={s} value={s}>{s}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Label className="text-xs">Expected value ({currencySymbol})</Label>
      <div className="flex items-center gap-1">
        <Input type="number" value={expected}
          onChange={(e) => setExpected(e.target.value)}
          data-testid="wi-lead-expected" />
        <Button size="sm" onClick={() => update({
          expected_value: parseFloat(expected) || 0,
          value_source: "MANAGER_ESTIMATE",
        })} disabled={saving}>Save</Button>
      </div>

      <Label className="text-xs">Follow-up date</Label>
      <div className="flex items-center gap-1">
        <Input type="date" value={followDate}
          onChange={(e) => setFollowDate(e.target.value)}
          data-testid="wi-lead-follow-date" />
        <Button size="sm" onClick={() => update({
          follow_up_required: true, follow_up_date: followDate,
        })} disabled={saving}>Set</Button>
      </div>

      <Label className="text-xs">Notes</Label>
      <Input value={notes} onChange={(e) => setNotes(e.target.value)}
        onBlur={() => notes !== lead.notes && update({ notes })}
        data-testid="wi-lead-notes" />

      <hr />
      <Label className="text-xs">Mark LOST</Label>
      <div className="flex gap-1">
        <Select value={lostReason || ""} onValueChange={setLostReason}>
          <SelectTrigger data-testid="wi-lead-lost-reason">
            <SelectValue placeholder="Reason" />
          </SelectTrigger>
          <SelectContent>
            {LOST_REASONS.map((r) => (
              <SelectItem key={r} value={r}>{r}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button size="sm" onClick={markLost} className="text-red-700 border-red-500"
          variant="outline" data-testid="wi-lead-mark-lost">
          LOST
        </Button>
      </div>
      {lostReason === "OTHER" && (
        <Input placeholder="Notes required for OTHER"
          value={lostNotes} onChange={(e) => setLostNotes(e.target.value)}
          data-testid="wi-lead-lost-notes" />
      )}

      {(lead.match?.state === "POSSIBLE_MATCH") && (
        <div className="flex gap-1">
          <Button size="sm" onClick={confirmMatch} className="bg-emerald-600"
            data-testid="wi-lead-confirm-match">
            Confirm Match
          </Button>
          <Button size="sm" variant="outline" onClick={rejectMatch}
            data-testid="wi-lead-reject-match">
            Reject Match
          </Button>
        </div>
      )}

      <hr />
      <div className="text-[10px] text-slate-500 italic">
        Future / disabled — awaiting Meta integration approval:
      </div>
      <div className="flex flex-wrap gap-1">
        {["Table Booking", "Catering", "Modak"].map((k) => (
          <Button key={k} size="sm" variant="outline" disabled
            title="Available after official WhatsApp integration"
            data-testid={`wi-send-link-${k.replace(/\s+/g,'-').toLowerCase()}`}>
            <Send className="w-3 h-3 mr-1" />
            Send {k} Link
          </Button>
        ))}
      </div>
    </div>
  );
}
